import cuid from "cuid";
import {
  defaultTo,
  defaultsDeep,
  filter,
  get,
  groupBy,
  head,
  isEmpty,
  isEqual,
  isNil,
  isNumber,
  isObject,
  keyBy,
  mapValues,
  set,
} from "lodash";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import {
  createFieldStore,
  FieldRegistryProvider,
} from "shared/hooks/useFieldRegistry";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";

import moment from "moment";
import { USER_AUTH } from "utils/api/graphql/queries/users";
import {
  ATTACHMENT_TYPES,
  BANK_ACCOUNT_OWNER_TYPE,
  CASE_TYPES,
  CONTACT_POINT_SYSTEM,
  PAYMENT_RECEIVER,
  PROJECT_SOURCE,
  PROJECT_TYPES,
  RELATIONSHIP,
  SALE_MODES,
  SITUATION_PROFESSIONAL,
  SUBSCRIPTION_FORM_KEYS,
  SUBSCRIPTION_STATUSES,
} from "utils/constants";
import {
  getLmfAdherentProofFields,
  getLmfPayerProofFields,
} from "./lmfProofDocuments";
import View from "./View";
import { showExerciseFrame } from "./Widgets/AdditionalInformation/Forms/Activity";

export const STEPPER_KEYS = {
  ADDITIONAL_INFORMATION: "ADDITIONAL_INFORMATION",
  PROOF: "PROOF",
  DOCUMENT: "DOCUMENT",
};

const ADHERENT_DOCUMENTS = [
  ATTACHMENT_TYPES.CIN,
  ATTACHMENT_TYPES.PAYMENT_ACCOUNT,
  ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
  ATTACHMENT_TYPES.PROOF_OF_ELIGIBILITY,
  ATTACHMENT_TYPES.KBIS,
  ATTACHMENT_TYPES.INPI,
];

const PAYER_DOCUMENTS = [
  ATTACHMENT_TYPES.PAYER_CIN,
  ATTACHMENT_TYPES.PAYER_RIB,
  ATTACHMENT_TYPES.CONTRIBUTION_PAYMENT_AGREEMENT,
];

const SPOUSE_DOCUMENTS = [
  ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
  ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
];
const CHILDREN_DOCUMENTS = [
  ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE,
  ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF,
  ATTACHMENT_TYPES.BANK_ACCOUNT_DETAILS_FOR_PAYMENT,
];

const Detail = () => {
  const { id } = useParams();
  const fieldStore = useMemo(() => createFieldStore(), []);
  const [initialValues, setInitialValues] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkUser] = useLazyQuery(USER_AUTH);
  const [updateSubscription] = useMutation(
    graphql.mutations.UPDATE_SUBSCRIPTION,
    {
      refetchQueries: [
        {
          query: graphql.queries.SUBSCRIPTION,
          awaitRefetchQueries: true,
          variables: { where: { id } },
        },
      ],
    },
  );

  const initSubscription = useCallback(
    ({ subscription: data, attachments }) => {
      const subscription = omitDeep(data, "__typename");
      const selfOccupation = !(
        get(subscription, "project.ria") &&
        get(subscription, "project.fields.selfOccupation") !== true
      );

      const subscriptionPayment = get(subscription, "payment");
      const paymentsTypes = get(
        subscription,
        "insurancePlan.fields.payment.type",
        [],
      );
      const debitDays = get(
        subscription,
        "insurancePlan.fields.payment.debitDay",
        [],
      );
      const frequencies = get(
        subscription,
        "insurancePlan.fields.payment.frequency",
        [],
      );
      const shouldBeAdherent =
        get(subscription, "payment.payer.owner.isAdherent", true) ||
        get(
          subscription,
          "insurancePlan.fields.payment.shouldBeAdherent",
          false,
        );
      if (isEmpty(subscription)) return;
      const contactTelecoms = get(subscription, "project.contact.telecoms", []);
      const payerTelecoms = get(
        subscription,
        "payment.payer.owner.telecoms",
        [],
      );
      const initialDisabled = {
        project: {
          ria: true,
          contract: {
            caseType: get(subscription, "project.ria", false),
            saleMode:
              get(subscription, "project.fields.twoTimesSale", false) ||
              get(subscription, "project.fields.source") ===
                PROJECT_SOURCE.CLIENT,
          },
          contact: {
            exerciseFrame: !showExerciseFrame(
              get(subscription, "project.contact.socioProfessionalCategory"),
            ),
            siretNumber: true,
            optMadelin: !get(subscription, "insurancePlan.fields.madelin"),
            fields: {
              affiliateOrganizationCode: true,
              socioProfessionalSubCategory:
                !get(
                  subscription,
                  "project.contact.socioProfessionalCategory",
                ) ||
                get(
                  subscription,
                  "project.contact.socioProfessionalCategory",
                ) === SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
            },
            profession: false,
          },
          projectType:
            get(subscription, "project.fields.source") ===
              PROJECT_SOURCE.CLIENT ||
            get(subscription, "project.fields.twoTimesSale") ||
            [SALE_MODES.FACE_TO_FACE].includes(
              get(subscription, "project.contract.saleMode"),
            ),
          terminationRequest: {
            contractNumber: selfOccupation,
            dueDate: selfOccupation,
            recipient: selfOccupation,
            reason: selfOccupation,
          },
        },
        createdDate: true,
        proof: {
          payerCin:
            true || get(subscription, "payment.payer.owner.isAdherent", true),
          refundAccount:
            true ||
            get(subscription, "payment.payer.id", "") ===
              get(subscription, "payment.receiver.id"),
        },
        telecoms: {
          payer: {
            firstName: shouldBeAdherent,
            lastName: shouldBeAdherent,
            [CONTACT_POINT_SYSTEM.STREET]: {
              value:
                shouldBeAdherent &&
                !get(subscription, "insurancePlan.fields.tns"),
            },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: {
              value:
                shouldBeAdherent &&
                !get(subscription, "insurancePlan.fields.tns"),
            },
            [CONTACT_POINT_SYSTEM.CITY]: {
              value:
                shouldBeAdherent &&
                !get(subscription, "insurancePlan.fields.tns"),
            },
          },
          contact: {},
        },
        // [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]:
        //   !get(subscription, "project.fields.selfOccupation", false),
        // // [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: !get(
        // //   subscription,
        // //   "project.ria",
        // //   false
        // // ),
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.SPOUSE),
        [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.CHILD),
        [SUBSCRIPTION_FORM_KEYS.PROOF.SPOUSE]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.SPOUSE),
        [SUBSCRIPTION_FORM_KEYS.PROOF.CHILDREN]: !get(
          subscription,
          "project.contact.relatedPersons",
          [],
        ).find((person) => person.relationship === RELATIONSHIP.CHILD),
        [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: get(
          subscription,
          "payment.payer.owner.isAdherent",
          true,
        ),
      };

      const telecoms = defaultsDeep(
        {
          contact: keyBy(contactTelecoms, "system"),
          payer: keyBy(
            shouldBeAdherent ? contactTelecoms : payerTelecoms,
            "system",
          ),
        },
        {
          contact: {
            // [CONTACT_POINT_SYSTEM.ADDRESS_NUMBER]: { value: null },
            // [CONTACT_POINT_SYSTEM.REPETITION_INDEX]: { value: null },
            // [CONTACT_POINT_SYSTEM.STREET]: { value: null },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
            [CONTACT_POINT_SYSTEM.CITY]: { value: null },
            [CONTACT_POINT_SYSTEM.PHONE]: { value: null },
            [CONTACT_POINT_SYSTEM.LANDLINE]: { value: null },
            [CONTACT_POINT_SYSTEM.EMAIL]: { value: null },
          },
          payer: {
            // [CONTACT_POINT_SYSTEM.ADDRESS_NUMBER]: { value: null },
            // [CONTACT_POINT_SYSTEM.REPETITION_INDEX]: { value: null },
            [CONTACT_POINT_SYSTEM.STREET]: { value: null },
            [CONTACT_POINT_SYSTEM.ZIP_CODE]: { value: null },
            [CONTACT_POINT_SYSTEM.CITY]: { value: null },
          },
        },
      );
      const children = filter(
        get(subscription, "project.contact.relatedPersons", []),
        {
          relationship: RELATIONSHIP.CHILD,
        },
      );
      if (!isEmpty(children))
        children.forEach((child) => {
          set(
            child,
            "identity.fields.teletransmission",
            get(child, "identity.fields.teletransmission", true),
          );
          set(
            child,
            "identity.telecoms",
            keyBy(get(child, "identity.telecoms", []), "system"),
          );
        });

      const spouse = get(
        subscription,
        "project.contact.relatedPersons",
        [],
      ).find(({ relationship }) => relationship === RELATIONSHIP.SPOUSE);
      if (!isEmpty(spouse)) {
        const spouseTelecoms = get(spouse, "identity.telecoms", []);
        set(spouse, "identity.telecoms", keyBy(spouseTelecoms, "system"));
      }
      const fields = {
        [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTRACT]: [
            "project.contract.issuanceDate",
            "project.contract.caseType",
            "project.ria",
            "project.contract.saleMode",
            "project.projectType",
            "createdDate",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST]: [
            "project.terminationRequest.contractNumber",
            "project.terminationRequest.recipient",
            "project.terminationRequest.reason",
            // "project.terminationRequest.dueDate",
          ],
          // [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.BROKERAGE_MANDATE]: [
          //   // "project.terminationRequest.brokerageMandate.lastName",
          //   // "project.terminationRequest.brokerageMandate.firstName",
          //   "project.terminationRequest.brokerageMandate.currentCompany",
          // ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ADHERENT]: [
            // "project.contact.user.gender",
            "project.contact.user.lastname",
            "project.contact.user.firstname",
            "project.contact.user.birthDate",
            "project.contact.socialRegime",
            "project.contact.socialSecurityNumber",
            "project.contact.fields.affiliateOrganizationCode",
            // "project.contact.fields.familySituation",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY]: [
            "project.contact.socioProfessionalCategory",
            "project.contact.fields.socioProfessionalSubCategory",
            "project.contact.fields.familySituation",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT]: [
            // `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS_NUMBER}.value`,
            // `telecoms.contact.${CONTACT_POINT_SYSTEM.REPETITION_INDEX}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.STREET}.value`,
            // `telecoms.contact.${CONTACT_POINT_SYSTEM.STREET_TYPE}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`,
            // `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
            `telecoms.contact.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
            // "project.contact.acceptContact",
            // "project.contact.acceptDigital",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONSENTS]: [
            // "project.contact.acceptContact",
            // "project.contact.acceptDigital",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PARTNER]: [
            "spouse.identity.user.firstname",
            "spouse.identity.user.lastname",
            "spouse.identity.user.birthDate",
            `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
            `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
            `spouse.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
            "spouse.identity.socialSecurityNumber",
            // "spouse.identity.socioProfessionalCategory",
            // "spouse.identity.socialRegime",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN]:
            children.flatMap((_, i) => [
              `children.${i}.identity.user.firstname`,
              `children.${i}.identity.user.lastname`,
              `children.${i}.identity.user.birthDate`,
              `children.${i}.identity.socialSecurityNumber`,
              `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_COUNTRY}.value`,
              `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.BIRTH_CITY}.value`,
              `children.${i}.identity.telecoms.${CONTACT_POINT_SYSTEM.DEPARTMENT_OF_BIRTH}.value`,
              // `children.${i}.identity.socialRegime`,
            ]),
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.PAYMENT]: [
            "payment.type",
            "payment.frequency",
            "payment.debitDay",
            "payment.payer.IBAN",
            "payment.payer.BIC",
            "payment.payer.owner.firstName",
            "payment.payer.owner.lastName",
            // ...(get(subscription, "insurancePlan.fields.tns")
            //   ? ["payment.payer.owner.socialReason"]
            //   : [
            //       "payment.payer.owner.firstName",
            //       "payment.payer.owner.lastName",
            //     ]),
            // `telecoms.payer.${CONTACT_POINT_SYSTEM.ADDRESS_NUMBER}.value`,
            // `telecoms.payer.${CONTACT_POINT_SYSTEM.STREET_TYPE}.value`,
            // `telecoms.payer.${CONTACT_POINT_SYSTEM.STREET}.value`,
            // `telecoms.payer.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
            // `telecoms.payer.${CONTACT_POINT_SYSTEM.CITY}.value`,
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.REFUND]: [
            "isPayerReceiver",
            "payment.receiver.owner.firstName",
            "payment.receiver.owner.lastName",
            "payment.receiver.IBAN",
            "payment.receiver.BIC",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION]: [
            "project.contact.fields.acceptTerms",
            "project.contact.fields.honorDeclaration",
            "project.contact.fields.digitalConsent",
            // "project.contact.fields.earlyExecConsent",
          ],
          [SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION
            .SPONSORSHIP_MEMBERSHIP]: [],
        },
        [STEPPER_KEYS.PROOF]: {
          [SUBSCRIPTION_FORM_KEYS.PROOF.ADHERENT]: getLmfAdherentProofFields(
            get(subscription, "project.contact"),
          ),
          [SUBSCRIPTION_FORM_KEYS.PROOF.SPOUSE]: [
            `proof.spouse.${ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE}`,
            // `proof.spouse.${ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF}`,
          ],
          [SUBSCRIPTION_FORM_KEYS.PROOF.CHILDREN]: children.flatMap(
            (child, i) => [
              moment().diff(get(child, "identity.user.birthDate"), "years") >=
              16
                ? `proof.children.${get(child, "identity.user.id")}.${
                    ATTACHMENT_TYPES.HEALTH_INSURANCE_RIGHTS_CERTIFICATE
                  }`
                : undefined,
              moment().diff(get(child, "identity.user.birthDate"), "years") >=
              18
                ? `proof.children.${get(child, "identity.user.id")}.${
                    ATTACHMENT_TYPES.CHILD_DEPENDENT_PROOF
                  }`
                : undefined,
            ],
          ),
          [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: getLmfPayerProofFields(
            get(subscription, "project.contact"),
          ),
        },
      };

      const additionalInfoInitialValues = {
        ...subscription,
        telecoms,
        project: {
          ...subscription.project,
          ...(subscription.project.fields.twoTimesSale && {
            projectType: PROJECT_TYPES.COLD_LEAD,
          }),
          contact: defaultsDeep(subscription.project.contact, {
            fields: { teletransmission: true },
            acceptContact: true,
          }),
          contract: {
            ...subscription.project.contract,
            ...(subscription.project.ria && {
              caseType: CASE_TYPES.RETURN_TO_COMPETITION,
            }),
            ...(subscription.project.fields.twoTimesSale && {
              saleMode: SALE_MODES.DISTANT,
            }),
          },
          ...(subscription.project.ria
            ? {
                terminationRequest: {
                  id: cuid(),
                  ...subscription.project.terminationRequest,
                  brokerageMandate: {
                    id: cuid(),
                    firstName: get(
                      subscription,
                      "project.contact.user.firstname",
                    ),
                    lastName: get(
                      subscription,
                      "project.contact.user.lastname",
                    ),
                    ...subscription.project.terminationRequest
                      ?.brokerageMandate,
                  },
                },
              }
            : {}),
        },
        isPayerReceiver:
          get(subscription, "payment.payer.id", "") ===
          get(subscription, "payment.receiver.id")
            ? PAYMENT_RECEIVER.SAME_ACCOUNT
            : PAYMENT_RECEIVER.ANOTHER_ACCOUNT,
        payment: {
          id: cuid(),
          ...subscriptionPayment,
          type: get(
            subscriptionPayment,
            "type",
            paymentsTypes.length === 1 ? head(paymentsTypes) : null,
          ),
          debitDay: get(
            subscriptionPayment,
            "debitDay",
            debitDays.length === 1 ? head(debitDays) : "5",
          ),
          frequency: get(
            subscriptionPayment,
            "frequency",
            frequencies.length === 1 ? head(frequencies) : null,
          ),
          payer: {
            id: cuid(),
            IBAN: null,
            BIC: null,
            ...subscription.payment?.payer,
            owner: {
              id: cuid(),
              ...subscription.payment?.payer?.owner,
              firstName: defaultTo(
                subscription.payment?.payer?.owner?.firstName,
                shouldBeAdherent
                  ? get(subscription, "project.contact.user.firstname")
                  : null,
              ),
              lastName: defaultTo(
                subscription.payment?.payer?.owner?.lastName,
                shouldBeAdherent
                  ? get(subscription, "project.contact.user.lastname")
                  : null,
              ),
              isAdherent: defaultTo(
                subscription.payment?.payer?.owner?.isAdherent,
                shouldBeAdherent,
              ),
              type: get(subscription, "insurancePlan.fields.tns")
                ? BANK_ACCOUNT_OWNER_TYPE.COMPANY
                : BANK_ACCOUNT_OWNER_TYPE.PARTICULAR,
            },
          },
          receiver: defaultsDeep(subscription.payment?.receiver, {
            id: cuid(),
            IBAN: null,
            BIC: null,
            owner: { id: cuid() },
          }),
        },
        children,
        spouse: isEmpty(spouse)
          ? undefined
          : defaultsDeep(spouse, {
              identity: { fields: { teletransmission: true } },
            }),
      };

      const adherentId = get(subscription, "project.contact.user.id");
      const spouseId = get(spouse, "identity.user.id");
      const groupAttachmentsByType = (userId, allowedTypes) =>
        mapValues(
          groupBy(
            filter(
              attachments,
              ({ user, type }) =>
                user?.id === userId && allowedTypes.includes(type),
            ).map(({ id, name, fileUrl, type }) => ({
              uid: id,
              name,
              status: "done",
              url: fileUrl,
              type,
            })),
            "type",
          ),
          (arr) => arr.map(({ type, ...rest }) => rest),
        );

      const childrenProof = children.reduce((acc, child) => {
        const childId = get(child, "identity.user.id");
        if (childId) {
          acc[childId] = groupAttachmentsByType(childId, CHILDREN_DOCUMENTS);
        }
        return acc;
      }, {});

      const proofInitialValues = {
        proof: {
          adherent: groupAttachmentsByType(adherentId, ADHERENT_DOCUMENTS),
          payer: groupAttachmentsByType(adherentId, PAYER_DOCUMENTS),
          spouse: groupAttachmentsByType(spouseId, SPOUSE_DOCUMENTS),
          children: childrenProof,
        },
      };

      setDisabled(initialDisabled);
      setInitialValues({
        [STEPPER_KEYS.ADDITIONAL_INFORMATION]: additionalInfoInitialValues,
        [STEPPER_KEYS.PROOF]: proofInitialValues,
        fields,
      });

      if (subscription.status === SUBSCRIPTION_STATUSES.SIGNED)
        setActive(STEPPER_KEYS.DOCUMENT);
      if (subscription.status === SUBSCRIPTION_STATUSES.PENDING)
        setActive(STEPPER_KEYS.DOCUMENT);
    },
    [],
  );

  const { data, loading, error } = useQuery(graphql.queries.SUBSCRIPTION, {
    variables: { where: { id } },
    onCompleted: ({ subscription }) => {
      getAttachments({
        onCompleted: ({ attachments: { data } }) =>
          initSubscription({ subscription, attachments: data }),
      });
    },
  });

  const [getAttachments] = useLazyQuery(graphql.queries.ATTACHMENTS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        AND: [{ subscription: { id: get(data, "subscription.id") } }],
      },
      isIn: {
        type: [
          ...ADHERENT_DOCUMENTS,
          ...PAYER_DOCUMENTS,
          ...SPOUSE_DOCUMENTS,
          ...CHILDREN_DOCUMENTS,
        ],
      },
    },
  });

  const [active, setActive] = useState(STEPPER_KEYS.ADDITIONAL_INFORMATION);
  const [disabled, setDisabled] = useState({});
  const [realProgress, setRealProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: 0,
    [STEPPER_KEYS.PROOF]: 0,
  });
  const [progress, setProgress] = useState({
    [STEPPER_KEYS.ADDITIONAL_INFORMATION]: {},
    [STEPPER_KEYS.PROOF]: {},
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [active]);

  const proofContactCategory = get(
    initialValues,
    `${STEPPER_KEYS.ADDITIONAL_INFORMATION}.project.contact.socioProfessionalCategory`,
  );
  const proofContactSubCategory = get(
    initialValues,
    `${STEPPER_KEYS.ADDITIONAL_INFORMATION}.project.contact.fields.socioProfessionalSubCategory`,
  );

  useEffect(() => {
    if (active !== STEPPER_KEYS.PROOF) return;

    setInitialValues((previous) => {
      if (isEmpty(previous)) return previous;

      const contact = get(
        previous,
        `${STEPPER_KEYS.ADDITIONAL_INFORMATION}.project.contact`,
      );
      const nextAdherentFields = getLmfAdherentProofFields(contact);
      const nextPayerFields = getLmfPayerProofFields(contact);
      const currentAdherentFields = get(
        previous,
        `fields.${STEPPER_KEYS.PROOF}.${SUBSCRIPTION_FORM_KEYS.PROOF.ADHERENT}`,
      );
      const currentPayerFields = get(
        previous,
        `fields.${STEPPER_KEYS.PROOF}.${SUBSCRIPTION_FORM_KEYS.PROOF.PAYER}`,
      );

      if (
        isEqual(currentAdherentFields, nextAdherentFields) &&
        isEqual(currentPayerFields, nextPayerFields)
      ) {
        return previous;
      }

      return {
        ...previous,
        fields: {
          ...previous.fields,
          [STEPPER_KEYS.PROOF]: {
            ...get(previous, `fields.${STEPPER_KEYS.PROOF}`, {}),
            [SUBSCRIPTION_FORM_KEYS.PROOF.ADHERENT]: nextAdherentFields,
            [SUBSCRIPTION_FORM_KEYS.PROOF.PAYER]: nextPayerFields,
          },
        },
      };
    });
  }, [active, proofContactCategory, proofContactSubCategory]);

  const calculateBlockProgress = (parent, block, values, disabled) => {
    if (disabled[block]) return;

    let fieldList = Object.values(
      get(initialValues, `fields.${parent}.${block}`, []),
    );

    if (parent === STEPPER_KEYS.ADDITIONAL_INFORMATION) {
      const registeredPaths = new Set(fieldStore.getRegisteredPaths());
      if (registeredPaths.size > 0) {
        fieldList = fieldList.filter((path) => registeredPaths.has(path));
      }
    }

    const maxLength = fieldList.filter((g) => g && !get(disabled, g)).length;

    const filedLength = fieldList.filter((g) => {
      const value = get(values, g);
      const isDisabled = get(disabled, g);

      if (isDisabled) return false;
      if (isNil(value)) return false;
      if (value === "") return false;

      if (block === SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONSENTS) {
        if (
          g === "project.contact.acceptContact" ||
          g === "project.contact.acceptDigital" ||
          g === "project.contact.fields.acceptTerms" ||
          g === "project.contact.fields.honorDeclaration" ||
          g === "project.contact.fields.digitalConsent" ||
          g === "project.contact.fields.earlyExecConsent"
        ) {
          return value === true;
        }
      }

      if (block === SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.INFORMATION) {
        if (
          g === "project.contact.fields.acceptTerms" ||
          g === "project.contact.fields.honorDeclaration" ||
          g === "project.contact.fields.digitalConsent" ||
          g === "project.contact.fields.earlyExecConsent"
        ) {
          return value === true;
        }
      }

      if (isObject(value) && isEmpty(value)) return false;

      return true;
    }).length;

    return maxLength ? Math.ceil((filedLength / maxLength) * 100) : 100;
  };

  const calculateRealTimeProgress = (
    parent,
    block,
    values,
    initialDisabled = disabled,
  ) => {
    setProgress((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [block]: calculateBlockProgress(parent, block, values, initialDisabled),
      },
    }));
  };

  const calculateProgressMean = (
    parent,
    values,
    initialDisabled = disabled,
  ) => {
    const { count, sum } = Object.keys(
      get(initialValues, `fields.${parent}`, []),
    ).reduce(
      (acc, block) => {
        const progress = calculateBlockProgress(
          parent,
          block,
          values,
          initialDisabled,
        );
        if (block === SUBSCRIPTION_FORM_KEYS.PROOF.SOCIAL_SECURITY_CERTIFICATE)
          return acc;
        return isNumber(progress)
          ? { sum: (acc.sum += progress), count: (acc.count += 1) }
          : acc;
      },
      { count: 0, sum: 0 },
    );
    const progress = +(sum / count).toFixed(2);

    setRealProgress((prev) => ({
      ...prev,
      [parent]: progress,
    }));

    if (
      parent === STEPPER_KEYS.PROOF &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...get(
                omitDeep(data, "__typename"),
                "subscription.additionalInfo",
                {},
              ),
              isDocumentsCompleted: progress === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    if (
      parent === STEPPER_KEYS.ADDITIONAL_INFORMATION &&
      ((progress === 100 && realProgress[parent] !== 100) ||
        (progress !== 100 && realProgress[parent] === 100))
    ) {
      return updateSubscription({
        variables: {
          where: { id },
          data: {
            additionalInfo: {
              ...get(values, "additionalInfo", {}),
              isInfoCompleted: progress === 100,
            },
          },
        },
        onCompleted: () => setIsSubmitting(false),
      });
    }
    setIsSubmitting(false);
  };

  if (loading || error || isEmpty(initialValues) || isEmpty(disabled))
    return <Loading />;

  return (
    <FieldRegistryProvider store={fieldStore}>
      <View
        active={active}
        setActive={setActive}
        data={omitDeep(data, "__typename")}
        progress={progress}
        setProgress={setProgress}
        disabled={disabled}
        setDisabled={setDisabled}
        setPercent={calculateRealTimeProgress}
        realProgress={realProgress}
        calculateProgressMean={calculateProgressMean}
        initialValues={initialValues}
        checkUser={checkUser}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </FieldRegistryProvider>
  );
};

export default Detail;
